/** The largest file a File upload answer may carry -- the server's own bound. */
export const TEMPLATE_FILE_MAX_BYTES = 5 * 1024 * 1024;

/** The formats a File upload is held to, where a form holds it to any (Purchase Requests, 2026-10-28). */
export interface TemplateFileFormats {
  /** The extensions it takes, lower case and without the dot. */
  extensions: readonly string[];
  /** The upload zone's hint. */
  hint: string;
  /** The formats as the error names them: "PDF, DOC, … or PNG". */
  names: string;
}

/**
 * A Purchase Request's File upload: the formats its hint has always named, and nothing else -- no `.exe`, `.js` or
 * `.html`. JPEG is JPG's other spelling. The server holds the request to the same list
 * (`TemplateFormValidator.PURCHASE_REQUEST_FILE_EXTENSIONS`).
 */
export const PURCHASE_REQUEST_FILE_FORMATS: TemplateFileFormats = {
  extensions: ['pdf', 'doc', 'docx', 'xls', 'xlsx', 'csv', 'jpg', 'jpeg', 'png'],
  hint: 'Supported formats: PDF, DOC/DOCX, XLS/XLSX, CSV, JPG, PNG',
  names: 'PDF, DOC, DOCX, XLS, XLSX, CSV, JPG or PNG',
};

/** Whether a file's name ends in one of the formats' extensions, in any case. */
export function hasFileFormat(fileName: string, formats: TemplateFileFormats): boolean {
  const dot = fileName.lastIndexOf('.');
  return dot > 0 && formats.extensions.includes(fileName.slice(dot + 1).toLowerCase());
}

/** A File upload answer's file name: its data URI's `name` parameter. */
export function fileNameOf(value: string): string {
  const match = /^data:[^,]*;name=([^;,]*)/.exec(value);
  if (!match?.[1]) return value.startsWith('data:') ? 'Attached file' : value;
  try {
    return decodeURIComponent(match[1]);
  } catch {
    return match[1];
  }
}

/**
 * The File upload data type's note in an added field's type list (the BID layout, 2026-10-21). The reference reads
 * "Max 10 MB per file"; the size named here is the platform's own bound ({@link TEMPLATE_FILE_MAX_BYTES}), so the note
 * never promises a file the answer would refuse.
 */
export const BID_FILE_UPLOAD_NOTE = `PDF, DOC, XLS, JPG, PNG · Max ${String(TEMPLATE_FILE_MAX_BYTES / (1024 * 1024))} MB per file`;
